import classNames from 'classnames';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import headerLogo from '@/assets/images/header-logo.svg';
import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Typeahead } from '@/design-system/atoms/Forms/Typeahead';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { LazyIconSearch } from '@/design-system/atoms/Icons/IconSearch/Lazy';
import { LazyIconX } from '@/design-system/atoms/Icons/IconX/Lazy';
import { Image } from '@/design-system/atoms/Image';
import { Text } from '@/design-system/atoms/Text';
import { Title } from '@/design-system/atoms/Title';
import { PatientGptCta } from '@/design-system/molecules/PatientGptCta';
import { useWindowSize } from '@/hooks/useWindowSize';
import { trackInteraction } from '@/utils/analytics';
import { scrollControl } from '@/utils/scrollControl';
import window from '@/utils/window';

import { useHeaderContext } from '../../contexts/HeaderContext';
import { headerPropTypes } from '../../headerPropTypes';
import { isActiveLanguage } from '../../utilities/languageSelector';
import styles from './DesktopHeader.module.scss';

const DesktopHeader = ({
    id,
    logoHref,
    searchLabelOverride = 'Search',
    searchAriaLabel = 'Open Global Site Search',
    mainNavAriaLabel = 'Open Main Navigation',
    closeNavAriaLabel = 'Close Main Navigation',
    backNavAriaLabel = 'Back to Main Navigation',
    navigationItems,
    search,
    closeSearchAriaLabel = 'Close Global Site Search',
    primaryCta,
    patientGptCta,
    utilityNavigationItems,
    isInEditor,
    coveo,
    searchResultsPage,
    disableCSR,
    hideLanguageSelector,
    onLanguageChange,
    isActive,
    ...props
}) => {
    const key = useId();
    const searchId = useId();
    const { width } = useWindowSize();

    const headerRef = useRef();
    const navListRef = useRef();
    const flyoutRefs = useRef(navigationItems?.map(React.createRef));
    const searchButtonRef = useRef();
    const searchTypeaheadRef = useRef();
    const languageRefs = useRef({});
    const dropdownRefs = useRef({});
    const [url, setUrl] = useState('');
    const [urlPathname, setUrlPathname] = useState('');
    const [urlSearch, setUrlSearch] = useState('');

    const [openDropdowns, setOpenDropdowns] = useState({});
    const [openNav, setOpenNav] = useState(null);
    const [maxHeight, setMaxHeight] = useState(0);
    const [leftPosition, setLeftPosition] = useState(0);
    const [searchOpen, setSearchOpen] = useState(false);
    const [languageLabel, setLanguageLabel] = useState('English');

    const { searchValue, suggestions, handleSetSearchValue, handleSelectSuggestion, submitForm } =
        useHeaderContext();

    const ToggleDropdown = useCallback((dropdownId) => {
        setOpenDropdowns((prev) => {
            const newState = { ...prev };
            Object.keys(newState).forEach((id) => {
                newState[id] = false;
                dropdownRefs.current[id]?.setAttribute('inert', '');
            });
            if (!prev[dropdownId]) {
                newState[dropdownId] = true;
                dropdownRefs.current[dropdownId]?.removeAttribute('inert');
            }

            return newState;
        });
    }, []);

    const ToggleSearch = useCallback(
        (skipFocus) => {
            if (searchOpen) {
                setOpenNav(null);
                setSearchOpen(false);
                scrollControl.enableScroll();
                if (!skipFocus) {
                    setTimeout(() => {
                        searchButtonRef?.current.focus();
                    }, 300);
                }

                // Analytics
                trackInteraction({
                    eventName: 'search',
                    componentName: 'Navigation',
                    componentTitle: 'navigation',
                    interactionType: 'close',
                    selector: 'search',
                    searchTerm: searchValue,
                });
            } else {
                setOpenNav(null);
                setSearchOpen(true);
                scrollControl.disableScroll();
                setTimeout(() => {
                    searchTypeaheadRef?.current.focus();
                }, 300);
            }
        },
        [searchOpen, setOpenNav, setSearchOpen, searchButtonRef, searchTypeaheadRef, searchValue],
    );

    const ToggleFlyOut = useCallback(
        (activeIndex) => {
            if (openNav === activeIndex) {
                setOpenNav(null);
                setMaxHeight(0);
            } else {
                setMaxHeight(
                    flyoutRefs?.current[activeIndex].current.getBoundingClientRect().height,
                );
                setOpenNav(activeIndex);
            }
        },
        [openNav, setOpenNav, flyoutRefs, setMaxHeight],
    );

    const resetHeader = useCallback(() => {
        if (searchOpen) {
            ToggleSearch(true);
        }

        setOpenDropdowns({});
        Object.values(dropdownRefs.current).forEach((ref) => {
            ref?.setAttribute('inert', '');
        });
        setOpenNav(null);
        setMaxHeight(0);
    }, [setOpenNav, setMaxHeight, ToggleSearch, searchOpen]);

    const onClickFocus = useCallback(
        (event) => {
            const isMobileHeader = event.target?.closest('header[data-header-type="mobile"]');
            if (!isMobileHeader && !headerRef?.current.contains(event.target)) {
                setSearchOpen(false);
                scrollControl.enableScroll();
                setOpenNav(null);
                setMaxHeight(0);
                setOpenDropdowns({});
                Object.values(dropdownRefs.current).forEach((ref) => {
                    ref?.setAttribute('inert', '');
                });
            }
        },
        [headerRef, setOpenNav, setMaxHeight],
    );

    const escKeyListen = useCallback(
        (event) => {
            if (event.key === 'Escape') {
                setOpenNav(null);
                setMaxHeight(0);
                setOpenDropdowns({});
                Object.values(dropdownRefs.current).forEach((ref) => {
                    ref?.setAttribute('inert', '');
                });
            }
        },
        [setOpenNav, setMaxHeight],
    );

    const handleLogoClick = useCallback(() => {
        trackInteraction({
            componentName: 'Navigation',
            componentTitle: 'navigation',
            interactionType: 'nav_logo',
            actionLabel: 'Atlantic Health System Homepage',
        });
    }, []);

    useEffect(() => {
        window.addEventListener('click', onClickFocus);
        window.addEventListener('focusin', onClickFocus);
        window.addEventListener('keyup', escKeyListen);
        window.addEventListener('keydown', escKeyListen);

        Object.values(dropdownRefs.current).forEach((ref) => {
            if (ref) {
                ref.setAttribute('inert', '');
            }
        });

        Object.keys(openDropdowns).forEach((dropdownId) => {
            if (openDropdowns[dropdownId] && dropdownRefs.current[dropdownId]) {
                dropdownRefs.current[dropdownId].removeAttribute('inert');
            }
        });

        return () => {
            window.removeEventListener('click', onClickFocus);
            window.removeEventListener('focusin', onClickFocus);
            window.removeEventListener('keyup', escKeyListen);
            window.removeEventListener('keydown', escKeyListen);
        };
    }, [openDropdowns, escKeyListen, onClickFocus]);

    useEffect(() => {
        var styles =
            navListRef?.current.currentStyle || window.getComputedStyle(navListRef?.current);

        if (styles.marginLeft === '0px') {
            setLeftPosition('20px');
        } else {
            setLeftPosition(styles.marginLeft);
        }
    }, [setLeftPosition, width]);

    // Set active language label
    useEffect(() => {
        Object.values(languageRefs?.current).forEach((el) => {
            const href = el?.getAttribute('href');

            if (
                url &&
                href &&
                isActiveLanguage({
                    activeUrl: url,
                    languageUrl: href,
                })
            ) {
                setLanguageLabel(el.getAttribute('label-setter'));
            }
        });
    }, [url]);

    useEffect(() => {
        if (!isActive) {
            resetHeader();
        }
    }, [isActive, resetHeader]);

    useEffect(() => {
        setUrl(window.location.origin);
        setUrlPathname(window.location.pathname);
        setUrlSearch(window.location.search);
    }, [setUrl, setUrlPathname, setUrlSearch]);

    return (
        <header className={styles[`desktop-header`]} ref={headerRef} {...props}>
            <div
                className={classNames(
                    styles[`desktop-header__overlay`],
                    (searchOpen || openNav !== null) && styles[`desktop-header__overlay--active`],
                    isInEditor && styles[`desktop-header__overlay--editor`],
                )}
            />
            {/* Start Utility Nav */}
            {Array.isArray(utilityNavigationItems) && utilityNavigationItems?.length > 0 && (
                <nav
                    aria-label="Utility Navigation"
                    role="menu"
                    className={classNames(
                        styles[`desktop-header__utility-wrapper`],
                        styles[`desktop-header--bg-linen`],
                    )}
                >
                    <ContentContainer className={styles[`desktop-header__utility`]}>
                        {Array.isArray(utilityNavigationItems) &&
                            utilityNavigationItems?.map((item, index) => {
                                const dropdownId = `utility-dropdown-${index}`;
                                const isLanguageDropdown =
                                    item?.label === 'English' ||
                                    item?.label === 'Español' ||
                                    item?.iconName === 'globe';
                                const isLanguageSelector =
                                    isLanguageDropdown && !hideLanguageSelector;
                                return (
                                    <div
                                        className={styles[`desktop-header__utility-nav`]}
                                        key={`utility-nav-${index}`}
                                    >
                                        {item?.dropdown &&
                                            !(isLanguageDropdown && hideLanguageSelector) && (
                                                <div
                                                    className={
                                                        styles[`desktop-header__dropdown-wrapper`]
                                                    }
                                                >
                                                    <Button
                                                        key={item?.label + index + key}
                                                        type="button"
                                                        buttonStyle={Button.STYLE.UTILITY}
                                                        label={
                                                            isLanguageSelector
                                                                ? languageLabel
                                                                : item?.label
                                                        }
                                                        aria-label={
                                                            isLanguageSelector
                                                                ? `Change site Language, currently ${languageLabel}`
                                                                : `Open ${item?.label} dropdown`
                                                        }
                                                        iconComponent={[
                                                            { iconName: item?.iconName },
                                                            {
                                                                iconName: 'chevron-down',
                                                                iconProps: {
                                                                    className: classNames(
                                                                        styles[
                                                                            `desktop-header__chevron`
                                                                        ],
                                                                        openDropdowns[dropdownId] &&
                                                                            styles[
                                                                                `desktop-header__chevron--open`
                                                                            ],
                                                                    ),
                                                                },
                                                            },
                                                        ]}
                                                        id={`utility-toggle-${index}`}
                                                        aria-controls={dropdownId}
                                                        aria-haspopup="true"
                                                        aria-expanded={
                                                            openDropdowns[dropdownId] || false
                                                        }
                                                        onClick={() => ToggleDropdown(dropdownId)}
                                                        role="menuitem"
                                                    />
                                                    <ul
                                                        ref={(el) => {
                                                            dropdownRefs.current[dropdownId] = el;
                                                        }}
                                                        className={classNames(
                                                            styles[
                                                                `desktop-header__utility-dropdown`
                                                            ],
                                                            openDropdowns[dropdownId] &&
                                                                styles[
                                                                    `desktop-header__utility-dropdown--open`
                                                                ],
                                                        )}
                                                        id={dropdownId}
                                                        role="menu"
                                                        aria-labelledby={`utility-toggle-${index}`}
                                                    >
                                                        {Array.isArray(item?.children) &&
                                                            item?.children?.map(
                                                                (child, childIndex) => {
                                                                    const href =
                                                                        isLanguageSelector &&
                                                                        onLanguageChange
                                                                            ? undefined
                                                                            : isLanguageSelector
                                                                              ? child?.href +
                                                                                urlPathname +
                                                                                urlSearch
                                                                              : child?.href;

                                                                    const onClick =
                                                                        isLanguageSelector &&
                                                                        onLanguageChange
                                                                            ? (e) => {
                                                                                  onLanguageChange(
                                                                                      e,
                                                                                      child?.label.toLowerCase(),
                                                                                  );
                                                                                  ToggleDropdown(
                                                                                      dropdownId,
                                                                                  );
                                                                              }
                                                                            : isLanguageSelector
                                                                              ? () =>
                                                                                    ToggleDropdown(
                                                                                        dropdownId,
                                                                                    )
                                                                              : () =>
                                                                                    ToggleDropdown(
                                                                                        dropdownId,
                                                                                    );

                                                                    return (
                                                                        <li
                                                                            role="none"
                                                                            key={`dropdown-item-${childIndex}`}
                                                                        >
                                                                            <Button
                                                                                ref={(el) => {
                                                                                    if (
                                                                                        isLanguageSelector
                                                                                    ) {
                                                                                        languageRefs.current =
                                                                                            {
                                                                                                ...languageRefs.current,
                                                                                                [child?.label]:
                                                                                                    el,
                                                                                            };
                                                                                    }
                                                                                }}
                                                                                key={
                                                                                    child?.label +
                                                                                    childIndex +
                                                                                    key
                                                                                }
                                                                                href={href}
                                                                                onClick={onClick}
                                                                                buttonStyle={
                                                                                    Button.STYLE
                                                                                        .UTILITY
                                                                                }
                                                                                label={child?.label}
                                                                                label-setter={
                                                                                    isLanguageSelector
                                                                                        ? child?.label
                                                                                        : undefined
                                                                                }
                                                                                aria-label={
                                                                                    isLanguageSelector
                                                                                        ? `Change site language to ${child?.label}`
                                                                                        : child?.ariaLabel ||
                                                                                          child?.label
                                                                                }
                                                                                role="menuitem"
                                                                                className={
                                                                                    isLanguageSelector
                                                                                        ? styles[
                                                                                              `desktop-header__language-option`
                                                                                          ]
                                                                                        : styles[
                                                                                              `desktop-header__dropdown-option`
                                                                                          ]
                                                                                }
                                                                                csr={
                                                                                    isLanguageSelector
                                                                                        ? false
                                                                                        : disableCSR
                                                                                          ? !disableCSR
                                                                                          : child?.csr
                                                                                }
                                                                                analytics={{
                                                                                    componentName:
                                                                                        'Navigation',
                                                                                    componentTitle:
                                                                                        'utility_nav',
                                                                                    interactionType:
                                                                                        'dropdown',
                                                                                }}
                                                                            />
                                                                        </li>
                                                                    );
                                                                },
                                                            )}
                                                    </ul>
                                                </div>
                                            )}
                                        {!item?.dropdown && (
                                            <Button
                                                {...item}
                                                key={item?.label + index + key}
                                                href={item?.href}
                                                buttonStyle={Button.STYLE.UTILITY}
                                                iconName={item?.iconName}
                                                label={item?.label}
                                                aria-label={item?.ariaLabel || item?.label}
                                                role="menuitem"
                                                analytics={{
                                                    componentName: 'Navigation',
                                                    componentTitle: 'utility_nav',
                                                    interactionType: 'internal_link',
                                                }}
                                                csr={disableCSR ? !disableCSR : item?.csr}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                    </ContentContainer>
                </nav>
            )}
            {/* End Utility Nav */}
            {/* Start Main Nav */}
            <nav
                aria-label="Main Navigation"
                role="menu"
                className={classNames(
                    styles[`desktop-header__main-nav`],
                    styles[`desktop-header--bg-white`],
                )}
            >
                <ContentContainer className={styles[`desktop-header__main-nav-container`]}>
                    <a
                        href={logoHref}
                        className={styles[`desktop-header__logo`]}
                        aria-label="Atlantic Health System Homepage"
                        role="menuitem"
                        onClick={handleLogoClick}
                        data-trigger={'a'}
                    >
                        <Image
                            src={headerLogo}
                            decorative
                            className={styles[`desktop-header__logo--image`]}
                        />
                    </a>
                    <div className={styles[`desktop-header__search-nav`]}>
                        <ul
                            className={styles[`desktop-header__nav-items`]}
                            role="none"
                            ref={navListRef}
                            {...(searchOpen ? { inert: '' } : {})}
                        >
                            {Array.isArray(navigationItems) &&
                                navigationItems?.map((item, index) => {
                                    return (
                                        <li
                                            key={`nav-item-${index}`}
                                            className={styles[`desktop-header__nav-item`]}
                                        >
                                            <Button
                                                buttonStyle={Button.STYLE.MAIN_NAV}
                                                className={classNames(
                                                    styles[`desktop-header__nav-item-button`],
                                                    openNav === index &&
                                                        styles[
                                                            `desktop-header__nav-item-button--active`
                                                        ],
                                                )}
                                                label={item?.parent}
                                                iconComponent={[
                                                    {
                                                        iconName: 'chevron-down',
                                                        iconProps: {
                                                            className: classNames(
                                                                styles[`desktop-header__chevron`],
                                                                openNav === index &&
                                                                    styles[
                                                                        `desktop-header__chevron--open`
                                                                    ],
                                                            ),
                                                        },
                                                    },
                                                ]}
                                                aria-controls={`nav-item-${index}`}
                                                aria-haspopup="true"
                                                aria-expanded={false}
                                                role="menuitem"
                                                onClick={() => ToggleFlyOut(index)}
                                                analytics={{
                                                    componentName: 'Navigation',
                                                    componentTitle: item?.parent,
                                                    interactionType: 'navigation',
                                                }}
                                            />
                                            <div
                                                role="none"
                                                className={classNames(
                                                    styles[`desktop-header__flyout`],
                                                    openNav === index &&
                                                        styles[`desktop-header__flyout--open`],
                                                )}
                                                style={{ '--max-height': `${maxHeight}px` }}
                                                {...(openNav !== index ? { inert: '' } : {})}
                                            >
                                                <div ref={flyoutRefs?.current[index]}>
                                                    <Grid
                                                        className={styles[`desktop-header__grid`]}
                                                    >
                                                        <GridColumn
                                                            className={
                                                                styles[`desktop-header__intro`]
                                                            }
                                                            colSpan={{ sm: 4, md: 6, lg: 3 }}
                                                        >
                                                            <Title
                                                                variant={Title.VARIANT.H5}
                                                                tag={Title.TAG.DIV}
                                                                content={item?.title}
                                                            />
                                                            {item?.description && (
                                                                <Text
                                                                    variant={Text.VARIANT.T3}
                                                                    tag={Text.TAG.P}
                                                                    content={item.description}
                                                                />
                                                            )}
                                                        </GridColumn>
                                                        <GridColumn
                                                            colSpan={{ sm: 4, md: 6, lg: 5 }}
                                                        >
                                                            <div
                                                                className={classNames(
                                                                    styles[`desktop-header__list`],
                                                                )}
                                                            >
                                                                {Array.isArray(item?.children) &&
                                                                    item?.children.map(
                                                                        (child, index) => {
                                                                            return (
                                                                                <Button
                                                                                    {...child}
                                                                                    id={`nav-item-${index}`}
                                                                                    key={`nav-item-${index}`}
                                                                                    href={
                                                                                        child?.href
                                                                                    }
                                                                                    buttonStyle={
                                                                                        Button.STYLE
                                                                                            .TERTIARY
                                                                                    }
                                                                                    label={
                                                                                        child?.label
                                                                                    }
                                                                                    aria-label={
                                                                                        child?.ariaLabel
                                                                                    }
                                                                                    role="menuitem"
                                                                                    className={
                                                                                        styles[
                                                                                            `desktop-header__nav-item`
                                                                                        ]
                                                                                    }
                                                                                    analytics={{
                                                                                        componentName:
                                                                                            'Navigation',
                                                                                        componentTitle:
                                                                                            item
                                                                                                ?.parent
                                                                                                ?.label,
                                                                                        interactionType:
                                                                                            'navigation',
                                                                                    }}
                                                                                    csr={
                                                                                        disableCSR
                                                                                            ? !disableCSR
                                                                                            : child?.csr
                                                                                    }
                                                                                    onClick={
                                                                                        resetHeader
                                                                                    }
                                                                                />
                                                                            );
                                                                        },
                                                                    )}
                                                                {Array.isArray(
                                                                    item?.categorized,
                                                                ) && (
                                                                    <>
                                                                        {item?.categorized.map(
                                                                            (item, index) => {
                                                                                return (
                                                                                    <div
                                                                                        className={classNames(
                                                                                            styles[
                                                                                                `desktop-header__list`
                                                                                            ],
                                                                                            styles[
                                                                                                `desktop-header__list--categorized`
                                                                                            ],
                                                                                        )}
                                                                                        key={index}
                                                                                    >
                                                                                        <Text
                                                                                            variant={
                                                                                                Text
                                                                                                    .VARIANT
                                                                                                    .E1
                                                                                            }
                                                                                            tag={
                                                                                                Text
                                                                                                    .TAG
                                                                                                    .P
                                                                                            }
                                                                                            content={
                                                                                                item?.label
                                                                                            }
                                                                                            className={classNames(
                                                                                                styles[
                                                                                                    `desktop-header__category-label`
                                                                                                ],
                                                                                            )}
                                                                                        />
                                                                                        {Array.isArray(
                                                                                            item?.children,
                                                                                        ) && (
                                                                                            <>
                                                                                                {item?.children.map(
                                                                                                    (
                                                                                                        child,
                                                                                                        index,
                                                                                                    ) => {
                                                                                                        return (
                                                                                                            <Button
                                                                                                                {...child}
                                                                                                                id={`nav-item-${index}`}
                                                                                                                key={`nav-item-${index}`}
                                                                                                                href={
                                                                                                                    child?.href
                                                                                                                }
                                                                                                                buttonStyle={
                                                                                                                    Button
                                                                                                                        .STYLE
                                                                                                                        .TERTIARY
                                                                                                                }
                                                                                                                label={
                                                                                                                    child?.label
                                                                                                                }
                                                                                                                aria-label={
                                                                                                                    child?.label
                                                                                                                }
                                                                                                                role="menuitem"
                                                                                                                className={
                                                                                                                    styles[
                                                                                                                        `desktop-header__nav-item`
                                                                                                                    ]
                                                                                                                }
                                                                                                                analytics={{
                                                                                                                    componentName:
                                                                                                                        'Navigation',
                                                                                                                    componentTitle:
                                                                                                                        item
                                                                                                                            ?.parent
                                                                                                                            ?.label,
                                                                                                                    interactionType:
                                                                                                                        'navigation',
                                                                                                                }}
                                                                                                                csr={
                                                                                                                    disableCSR
                                                                                                                        ? !disableCSR
                                                                                                                        : child?.csr
                                                                                                                }
                                                                                                            />
                                                                                                        );
                                                                                                    },
                                                                                                )}
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            },
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </GridColumn>
                                                        <GridColumn
                                                            colSpan={{ sm: 4, md: 6, lg: 4 }}
                                                        >
                                                            {/* menu card */}
                                                            {item?.menuCardComponent}
                                                        </GridColumn>
                                                    </Grid>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                        </ul>
                        <div
                            className={classNames(
                                styles[`desktop-header__actions`],
                                Boolean(patientGptCta?.label) &&
                                    styles[`desktop-header__actions--with-patient-gpt-cta`],
                            )}
                            data-testid="desktop-header__actions"
                            {...(searchOpen ? { inert: '' } : {})}
                        >
                            {/* Primary CTA */}
                            {primaryCta && (
                                <div className={styles[`desktop-header__primary-cta`]} role="none">
                                    <Button
                                        {...primaryCta}
                                        buttonStyle={Button.STYLE.PRIMARY}
                                        role="menuitem"
                                        analytics={{
                                            componentName: 'Navigation',
                                            componentTitle: 'navigation',
                                        }}
                                        csr={disableCSR ? !disableCSR : primaryCta?.csr}
                                    />
                                </div>
                            )}
                            {/* PatientGPT CTA */}
                            {Boolean(patientGptCta?.label) && (
                                // role="none" strips the wrapper's implicit role so assistive tech
                                // ignores it (it is never focusable, no tabIndex) — matching the
                                // primaryCta pattern above. role="menuitem" is forwarded into
                                // PatientGptCta so it lands on the composed Link, the only element
                                // that actually receives focus, satisfying aria-required-children
                                // for the ancestor role="menu" nav (a11y review finding).
                                <div role="none">
                                    <PatientGptCta
                                        href={patientGptCta.href}
                                        label={patientGptCta.label}
                                        ariaLabel={patientGptCta.ariaLabel}
                                        role="menuitem"
                                        analytics={{
                                            componentName: 'Navigation',
                                            componentTitle: 'navigation',
                                        }}
                                    />
                                </div>
                            )}
                            {/* Search */}
                            <Button
                                ref={searchButtonRef}
                                ariaLabel={searchAriaLabel}
                                buttonStyle={Button.STYLE.TERTIARY}
                                iconComponent={LazyIconSearch}
                                className={styles[`desktop-header__search`]}
                                role="menuitem"
                                aria-expanded={searchOpen}
                                aria-haspopup="true"
                                aria-controls={searchId}
                                onClick={ToggleSearch}
                            />
                        </div>
                        {/* Search Bar */}
                        <form
                            id={searchId}
                            className={classNames(
                                styles[`desktop-header__search-bar`],
                                searchOpen && styles[`desktop-header__search-bar--open`],
                            )}
                            style={{ '--left-position': leftPosition }}
                            {...(!searchOpen ? { inert: '' } : {})}
                            onSubmit={submitForm}
                            data-trigger="search"
                        >
                            <div className={styles[`desktop-header__search-bar-wrapper`]}>
                                <Typeahead
                                    ref={searchTypeaheadRef}
                                    label={searchLabelOverride}
                                    buttonProps={{
                                        type: 'submit',
                                        iconComponent: LazyIconSearch,
                                        ariaLabel: 'search',
                                    }}
                                    items={suggestions}
                                    inputValue={searchValue}
                                    onInputChange={handleSetSearchValue}
                                    onSelectionItemChange={handleSelectSuggestion}
                                    wrapperClassName={styles[`desktop-header__search-bar-input`]}
                                    popoverClassName={styles[`desktop-header__search-bar-popover`]}
                                />
                                <Button
                                    ariaLabel={closeSearchAriaLabel}
                                    buttonStyle={Button.STYLE.TERTIARY}
                                    iconComponent={LazyIconX}
                                    className={styles[`desktop-header__close`]}
                                    type="button"
                                    onClick={ToggleSearch}
                                />
                            </div>
                        </form>
                    </div>
                </ContentContainer>
            </nav>
            {/* End Main Nav */}
        </header>
    );
};

DesktopHeader.propTypes = {
    ...headerPropTypes,
};

export default DesktopHeader;
